import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Layout } from "../../components/Layout";
import { Logo } from "../../components/Logo";
import { Navbar } from "../../components/Navbar";
import { Select } from "../../components/Select";
import { Body, Display, Muted } from "../../components/Typography";
import { ApiError, api } from "../../lib/api";
import type {
  CameraResponse,
  EdgeAgentResponse,
  StoreResponse,
  UpdateCameraRequest,
} from "../../lib/api";
import { COUNTRIES, countryName } from "../../lib/countries";
import { US_STATES } from "../../lib/usStates";
import { useBackGuard } from "../../lib/useBackGuard";
import { WatchInstructionsEditor } from "../CameraView/WatchInstructionsEditor";
import {
  HardwareFields,
  type HardwareState,
  customHardwareState,
  emptyHardwareState,
  resolveHardware,
} from "../StoreDetail/HardwareFields";
import { kindAt, matchKindIndex } from "../StoreDetail/edgeBoxKinds";
import { BillingStep } from "./BillingStep";
import { ConnectEdgeAgentStep } from "./ConnectEdgeAgentStep";
import styles from "./SetupWizard.module.css";

type WizardStep = 1 | 2 | 3 | 4 | 5;

const DEFAULT_AGENT_NAME = "Shobdo Edge Agent";
const ONLINE_POLL_MS = 30_000;

type AddressForm = {
  street: string;
  unit: string;
  city: string;
  state: string;
  zipcode: string;
  country: string; // ISO 3166-1 alpha-2
  timezone: string; // explicit override; "" = inferred
};
type CameraDetailState = {
  location_label: string;
  looking_at: string;
  overlap_camera_ids: string[];
  additional_description: string;
};

// Billing is the LAST step (operator product decision 2026-08-24): the customer sets up
// hardware and cameras first and commits to a plan at the end.
const STEPS: Record<WizardStep, { kicker: string; title: string; sub: string }> = {
  1: { kicker: "01", title: "Store details", sub: "Profile, address, and AI context." },
  2: { kicker: "02", title: "Edge Agent Hardware", sub: "Name the agent and pick its edge box." },
  3: { kicker: "03", title: "Connect Edge Agent", sub: "Install the device and save its key." },
  4: {
    kicker: "04",
    title: "Describe camera feeds",
    sub: "Location, coverage, and what to watch for.",
  },
  5: { kicker: "05", title: "Billing", sub: "Plan, free trial, and payment card." },
};

const STEP_HEADINGS: Record<WizardStep, string> = {
  1: "Tell us about the shop.",
  2: "Set up the edge agent hardware.",
  3: "Connect your edge agent.",
  4: "Describe each camera and what to watch for.",
  5: "Pick a plan and start your free trial.",
};

const STEP_BLURBS: Record<WizardStep, string> = {
  1: "Store context helps reports distinguish normal activity from suspicious activity.",
  2: "Give the agent a name and choose the edge box it runs on — its camera capacity comes from the hardware.",
  3: "The edge agent auto-discovers cameras on the store network. Save its API key and follow the install steps.",
  4: "Add each camera's placement, field of view and shared coverage, then tell the AI what to watch for — across the store and per camera. Come back anytime as the agent discovers more cameras.",
  5: "Pick the plan that fits your camera count. The first 7 days are free — your card is saved now and nothing is charged until the trial ends.",
};

const composeAddress = (address: AddressForm): string => {
  const line1 = [address.street.trim(), address.unit.trim()].filter(Boolean).join(", ");
  const line2 = [address.city.trim(), address.state.trim(), address.zipcode.trim()]
    .filter(Boolean)
    .join(", ");
  return [line1, line2, countryName(address.country)].filter(Boolean).join(", ");
};

// Default only — the wizard shows an editable Time zone select beside it, so a wrong
// guess is one click from the truth (2026-08-24: the old US-locked wizard stamped a
// Dhaka store America/New_York — wrong periods AND a wrong billing cycle).
const inferTimezone = (address: AddressForm): string => {
  const country = address.country.trim().toUpperCase();
  const state = address.state.trim().toUpperCase();
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (country === "BD") return "Asia/Dhaka";
  if (country === "CA") return "America/Toronto";
  if (country === "GB") return "Europe/London";
  if (country === "DE") return "Europe/Berlin";
  if (country === "US") {
    if (["AK"].includes(state)) return "America/Anchorage";
    if (["HI"].includes(state)) return "Pacific/Honolulu";
    if (["CA", "NV", "OR", "WA"].includes(state)) return "America/Los_Angeles";
    if (["AZ"].includes(state)) return "America/Phoenix";
    if (["CO", "ID", "MT", "NM", "UT", "WY"].includes(state)) return "America/Denver";
    if (["AL", "AR", "IA", "IL", "LA", "MN", "MO", "MS", "OK", "WI"].includes(state)) {
      return "America/Chicago";
    }
    return "America/New_York";
  }

  return browserTimezone || "UTC";
};

const TIMEZONES: string[] = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return ["UTC"];
  }
})();

const emptyCameraDetail = (): CameraDetailState => ({
  location_label: "",
  looking_at: "",
  overlap_camera_ids: [],
  additional_description: "",
});

const buildCameraDescription = (
  detail: CameraDetailState,
  cameras: CameraResponse[],
): string | null => {
  const overlapNames = detail.overlap_camera_ids
    .map((id) => cameras.find((camera) => camera.id === id)?.name)
    .filter((name): name is string => Boolean(name));
  const parts = [
    detail.looking_at.trim() ? `Looking at: ${detail.looking_at.trim()}` : null,
    overlapNames.length > 0 ? `Shared coverage with: ${overlapNames.join(", ")}` : null,
    detail.additional_description.trim() ? `Notes: ${detail.additional_description.trim()}` : null,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join("\n") : null;
};

// Resume skip affordance: "Skip for now" at the (terminal) billing step is offered only
// when the store actually predates billing (the server's predates_billing flag —
// created before the seeding migration) AND still has no subscription row — §7's
// absent-row safety default. A post-billing rowless store, and a born-dark
// pending_billing one, get the same mandatory card step as a new store (PRD §4).
const resumeBillingSkippable = async (storeId: string): Promise<boolean> => {
  try {
    const config = await api.getBillingConfig();
    if (!config.publishable_key) return false;
    const summary = await api.getBillingSummary(storeId);
    return summary.subscription === null && summary.predates_billing;
  } catch {
    return false;
  }
};

// A camera counts as described once a describe-step field has been saved for it. Watch
// instructions are not one: they live in agent.watch_items, and the legacy
// system_instructions column is unread and never written here.
const cameraDescribed = (camera: CameraResponse): boolean =>
  camera.location_label !== null || camera.description !== null;

export const SetupWizard = () => {
  const navigate = useNavigate();
  const { storeId: resumeStoreId } = useParams<{ storeId: string }>();

  const [step, setStep] = useState<WizardStep>(1);
  // Highest step reached this session — visited steps are clickable in the sidebar
  // (both directions); billing (5) is excluded from re-entry, it manages itself.
  const [maxStep, setMaxStep] = useState<WizardStep>(1);
  const [store, setStore] = useState<StoreResponse | null>(null);
  const [edgeAgent, setEdgeAgent] = useState<EdgeAgentResponse | null>(null);
  const [edgeApiKey, setEdgeApiKey] = useState<string | null>(null);
  const [edgeKeyAcknowledged, setEdgeKeyAcknowledged] = useState(false);
  const [online, setOnline] = useState(false);
  const [checkingOnline, setCheckingOnline] = useState(false);
  const [cameras, setCameras] = useState<CameraResponse[]>([]);
  const [cameraDetails, setCameraDetails] = useState<Record<string, CameraDetailState>>({});
  const [loadingResume, setLoadingResume] = useState<boolean>(Boolean(resumeStoreId));

  const [allowBillingSkip, setAllowBillingSkip] = useState(false);
  const [agentName, setAgentName] = useState(DEFAULT_AGENT_NAME);
  const [hardware, setHardware] = useState<HardwareState>(emptyHardwareState());

  const [storeForm, setStoreForm] = useState({
    name: "",
    street: "",
    unit: "",
    city: "",
    state: "",
    zipcode: "",
    country: "US",
    timezone: "",
    description: "",
  });

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);

  const exitToStores = useCallback(() => navigate("/stores"), [navigate]);
  useBackGuard(true, "Leave setup? You can finish it later from your stores.", exitToStores);

  const leaveWizard = useCallback(() => navigate("/stores"), [navigate]);

  const loadCameras = useCallback(async (id: string): Promise<CameraResponse[]> => {
    const list = await api.listStoreCameras(id);
    setCameras(list);
    setCameraDetails((prev) => {
      const next = { ...prev };
      for (const cam of list) next[cam.id] ??= emptyCameraDetail();
      return next;
    });
    return list;
  }, []);

  const refreshOnline = useCallback(async (): Promise<void> => {
    if (!store) return;
    setCheckingOnline(true);
    try {
      const agents = await api.listStoreEdgeAgents(store.id);
      const match = agents.find((a) => a.id === edgeAgent?.id) ?? agents[0];
      if (match) {
        setEdgeAgent(match);
        setOnline(match.status === "online");
      }
    } catch {
      // Non-fatal; the operator can Check now again.
    } finally {
      setCheckingOnline(false);
    }
  }, [store, edgeAgent?.id]);

  // Resume: load an existing store and land on the earliest genuinely incomplete step —
  // the same place a continuous session would be. Billing is the wizard's LAST step and
  // is reached only in its natural terminal position (operator ruling 2026-08-26; the
  // born-dark gate already guarantees an unfinished store costs nothing, so billing is
  // never front-loaded on resume).
  useEffect(() => {
    if (!resumeStoreId) return;
    let cancelled = false;
    (async () => {
      try {
        const loaded = await api.getStore(resumeStoreId);
        if (cancelled) return;
        setStore(loaded);
        const agents = await api.listStoreEdgeAgents(resumeStoreId);
        const list = await loadCameras(resumeStoreId);
        if (cancelled) return;
        const acknowledged = loaded.edge_key_acknowledged_at !== null;
        let target: WizardStep;
        if (list.length > 0) {
          if (agents[0]) setEdgeAgent(agents[0]);
          setEdgeKeyAcknowledged(acknowledged);
          setOnline(agents[0]?.status === "online");
          target = list.every(cameraDescribed) ? 5 : 4;
        } else if (acknowledged && agents[0]) {
          setEdgeAgent(agents[0]);
          setEdgeKeyAcknowledged(true);
          setOnline(agents[0].status === "online");
          target = 3;
        } else if (agents[0]) {
          // Agent created but key never acknowledged. Resume must land back on the connect
          // step — the step the operator actually left (2026-08-26; previously this path
          // bounced them to hardware).
          const a = agents[0];
          setAgentName(a.name ?? DEFAULT_AGENT_NAME);
          const idx = matchKindIndex(a.hardware_model);
          setHardware(
            kindAt(idx).custom
              ? customHardwareState(a.hardware_model ?? "", a.allowed_number_of_cameras)
              : { index: idx, customModel: "", customCapacity: "" },
          );
          if (a.status === "online") {
            // The device is already running with this key — the key evidently reached it,
            // so stamp the acknowledgement instead of asking for a checkbox whose purpose
            // is already served (deleting an online agent would also strand the device).
            setEdgeAgent(a);
            setOnline(true);
            setEdgeKeyAcknowledged(true);
            api
              .acknowledgeStoreEdgeKey(resumeStoreId)
              .then((updated) => setStore(updated))
              .catch(() => {
                // Local flag still lets the operator proceed; the stamp retries on next mutation.
              });
          } else {
            // The key can't be re-shown, so reissue: replace the agent with an identical
            // one and show the fresh key on the connect step.
            await api.deleteEdgeAgent(resumeStoreId, a.id);
            const provisioned = await api.createEdgeAgent(resumeStoreId, {
              name: a.name ?? DEFAULT_AGENT_NAME,
              hardware_model: a.hardware_model,
              allowed_number_of_cameras: a.allowed_number_of_cameras,
            });
            setEdgeAgent(provisioned.edge_agent);
            setEdgeApiKey(provisioned.api_key);
          }
          target = 3;
        } else {
          target = 2;
        }
        if (cancelled) return;
        const skippable = await resumeBillingSkippable(resumeStoreId);
        if (cancelled) return;
        setAllowBillingSkip(skippable);
        setStep(target);
      } catch (e) {
        if (cancelled) return;
        setError(
          e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't load this store.",
        );
      } finally {
        if (!cancelled) setLoadingResume(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resumeStoreId, loadCameras]);

  // Poll the agent's online status while waiting on the Connect step (only while mounted).
  useEffect(() => {
    if (step !== 3 || !edgeKeyAcknowledged || online) return;
    void refreshOnline();
    const id = window.setInterval(() => void refreshOnline(), ONLINE_POLL_MS);
    return () => window.clearInterval(id);
  }, [step, edgeKeyAcknowledged, online, refreshOnline]);

  // Step-1 fields lock once the store row exists (back-navigation re-entry):
  // edits here would silently not save — Store Settings owns post-create edits.
  const storeLocked = store !== null;

  const handleStore = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (store) {
      setStep(2);
      return;
    }
    setSubmitting(true);
    try {
      const address = composeAddress(storeForm);
      const created = await api.createStore({
        name: storeForm.name.trim(),
        address: address || null,
        timezone: storeForm.timezone || inferTimezone(storeForm),
        description: storeForm.description.trim() || null,
      });
      setStore(created);
      setStep(2);
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't create store.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleHardware = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!store) return;
    if (edgeAgent) {
      // Re-entered via back-navigation: the agent (and its one-time key) already
      // exist — never provision a duplicate. Hardware changes live in the store's
      // Agents panel after setup.
      setStep(3);
      return;
    }
    const value = resolveHardware(hardware);
    if (!value || agentName.trim() === "") return;
    setError(null);
    setProvisionError(null);
    setSubmitting(true);
    try {
      const provisioned = await api.createEdgeAgent(store.id, { name: agentName.trim(), ...value });
      setEdgeAgent(provisioned.edge_agent);
      setEdgeApiKey(provisioned.api_key);
      setStep(3);
    } catch (e) {
      setError(
        e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't provision the edge agent.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const acknowledgeKey = async (): Promise<void> => {
    if (!store || edgeKeyAcknowledged) return;
    setEdgeKeyAcknowledged(true);
    try {
      const updated = await api.acknowledgeStoreEdgeKey(store.id);
      setStore(updated);
    } catch {
      // The local flag still lets the operator proceed; the stamp will retry on next mutation.
    }
  };

  const goToDescribe = async (): Promise<void> => {
    if (!store) return;
    setError(null);
    try {
      await loadCameras(store.id);
    } catch {
      // Non-fatal — the describe step shows an empty state and can be retried.
    }
    setStep(4);
  };

  const handleCameraDetails = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!store) return;
    setError(null);
    setSubmitting(true);
    try {
      for (const cam of cameras) {
        const detail = cameraDetails[cam.id] ?? emptyCameraDetail();
        const patch: UpdateCameraRequest = {};
        const location = detail.location_label.trim();
        const description = buildCameraDescription(detail, cameras);
        if (location) patch.location_label = location;
        if (description) patch.description = description;
        if (Object.keys(patch).length > 0) {
          await api.updateCamera(cam.id, patch);
        }
      }
      // Billing is the terminal step: setup completes only after it resolves there.
      setStep(5);
    } catch (e) {
      setError(
        e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't save camera details.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const finishSetup = async (): Promise<void> => {
    if (!store) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.completeStoreSetup(store.id);
      navigate(`/stores/${store.id}`, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't complete setup.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateCameraDetail = (cameraId: string, patch: Partial<CameraDetailState>): void => {
    setCameraDetails((prev) => ({
      ...prev,
      [cameraId]: { ...(prev[cameraId] ?? emptyCameraDetail()), ...patch },
    }));
  };

  const toggleOverlap = (cameraId: string, overlapId: string): void => {
    const current = cameraDetails[cameraId] ?? emptyCameraDetail();
    const nextIds = current.overlap_camera_ids.includes(overlapId)
      ? current.overlap_camera_ids.filter((id) => id !== overlapId)
      : [...current.overlap_camera_ids, overlapId];
    updateCameraDetail(cameraId, { overlap_camera_ids: nextIds });
  };

  useEffect(() => {
    setMaxStep((m) => (step > m ? step : m));
  }, [step]);

  const stepState = (n: WizardStep): "done" | "active" | "todo" => {
    if (n < step) return "done";
    if (n === step) return "active";
    return "todo";
  };

  const stepClickable = (n: WizardStep): boolean =>
    n !== step && n <= maxStep && n !== 5 && !submitting;

  const isCustomHardware = edgeAgent
    ? kindAt(matchKindIndex(edgeAgent.hardware_model)).custom
    : kindAt(hardware.index).custom;

  if (loadingResume) {
    return (
      <Layout nav={<Navbar brand={<Logo />} sectionLabel="Store setup" avatar={undefined} />}>
        <main className={styles.content}>
          <Body>
            <Muted>Loading your store…</Muted>
          </Body>
        </main>
      </Layout>
    );
  }

  return (
    <Layout
      nav={
        <Navbar
          brand={
            <button
              type="button"
              onClick={leaveWizard}
              aria-label="Go to your stores"
              style={{
                display: "inline-flex",
                textDecoration: "none",
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              <Logo />
            </button>
          }
          sectionLabel={store ? "Store setup" : "New store setup"}
          rightActions={
            <li className="nav-item">
              <button
                type="button"
                className="btn btn-link nav-link"
                onClick={() => navigate("/stores")}
              >
                {store ? "Save & exit" : "Cancel"}
              </button>
            </li>
          }
          avatar={undefined}
        />
      }
    >
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <div>
            <div className={styles.sidebarKicker}>Setup · Step {step} of 5</div>
            <div className={styles.sidebarTitle}>{STEPS[step].title}</div>
            <p className={styles.sidebarBody}>{STEP_BLURBS[step]}</p>
          </div>

          <ol className={styles.stepList}>
            {[1, 2, 3, 4, 5].map((n) => {
              const state = stepState(n as WizardStep);
              const desc = STEPS[n as WizardStep];
              return (
                <li key={n} className={styles.step} data-state={state}>
                  <button
                    type="button"
                    className={styles.stepButton}
                    onClick={() => stepClickable(n as WizardStep) && setStep(n as WizardStep)}
                    disabled={!stepClickable(n as WizardStep)}
                    aria-label={`Go to step ${n}: ${desc.title}`}
                    data-clickable={stepClickable(n as WizardStep) || undefined}
                  >
                    <span aria-hidden="true" className={styles.stepIndex}>
                      {state === "done" ? "✓" : n}
                    </span>
                    <div>
                      <div className={styles.stepKicker}>
                        {desc.kicker} ·{" "}
                        {state === "done" ? "DONE" : state === "active" ? "YOU'RE HERE" : ""}
                      </div>
                      <div className={styles.stepTitle}>{desc.title}</div>
                      <div className={styles.stepSub}>{desc.sub}</div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        <main className={styles.content}>
          <header className={styles.header}>
            <span className={styles.kicker}>Step {step.toString().padStart(2, "0")}</span>
            <Display>{STEP_HEADINGS[step]}</Display>
            <Body>
              <Muted>{STEP_BLURBS[step]}</Muted>
            </Body>
          </header>

          {step === 1 && (
            <form className={styles.form} onSubmit={handleStore} noValidate>
              {error && (
                <div className={styles.banner}>
                  <div>
                    <div className={styles.bannerTitle}>Couldn't save the store</div>
                    <div className={styles.bannerBody}>{error}</div>
                  </div>
                </div>
              )}
              {storeLocked && (
                <Body>
                  <Muted>
                    Store details are saved — edit them anytime in Store Settings after setup.
                  </Muted>
                </Body>
              )}
              <div className={styles.grid2}>
                <div className={styles.field}>
                  <label htmlFor="store-name" className={styles.label}>
                    Store name
                  </label>
                  <Input
                    id="store-name"
                    required
                    value={storeForm.name}
                    onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                    disabled={submitting || storeLocked}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="store-country" className={styles.label}>
                    Country
                  </label>
                  <Select
                    id="store-country"
                    value={storeForm.country}
                    onChange={(e) =>
                      setStoreForm({
                        ...storeForm,
                        country: e.target.value,
                        state: "",
                        timezone: "",
                      })
                    }
                    disabled={submitting || storeLocked}
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className={styles.field}>
                <label htmlFor="store-street" className={styles.label}>
                  Street address
                </label>
                <Input
                  id="store-street"
                  value={storeForm.street}
                  onChange={(e) => setStoreForm({ ...storeForm, street: e.target.value })}
                  disabled={submitting || storeLocked}
                />
              </div>
              <div className={styles.grid2}>
                <div className={styles.field}>
                  <label htmlFor="store-unit" className={styles.label}>
                    Apt / Unit
                  </label>
                  <Input
                    id="store-unit"
                    value={storeForm.unit}
                    onChange={(e) => setStoreForm({ ...storeForm, unit: e.target.value })}
                    disabled={submitting || storeLocked}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="store-city" className={styles.label}>
                    City
                  </label>
                  <Input
                    id="store-city"
                    value={storeForm.city}
                    onChange={(e) => setStoreForm({ ...storeForm, city: e.target.value })}
                    disabled={submitting || storeLocked}
                  />
                </div>
              </div>
              <div className={styles.grid2}>
                <div className={styles.field}>
                  <label htmlFor="store-state" className={styles.label}>
                    {storeForm.country === "US" ? "State" : "State / Province / Region"}
                  </label>
                  {storeForm.country === "US" ? (
                    <Select
                      id="store-state"
                      value={storeForm.state}
                      onChange={(e) => setStoreForm({ ...storeForm, state: e.target.value })}
                      disabled={submitting || storeLocked}
                    >
                      <option value="">Select a state…</option>
                      {US_STATES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      id="store-state"
                      value={storeForm.state}
                      onChange={(e) => setStoreForm({ ...storeForm, state: e.target.value })}
                      disabled={submitting || storeLocked}
                    />
                  )}
                </div>
                <div className={styles.field}>
                  <label htmlFor="store-zipcode" className={styles.label}>
                    {storeForm.country === "US" ? "Zipcode" : "Postal code"}
                  </label>
                  <Input
                    id="store-zipcode"
                    value={storeForm.zipcode}
                    onChange={(e) => setStoreForm({ ...storeForm, zipcode: e.target.value })}
                    disabled={submitting || storeLocked}
                  />
                </div>
              </div>
              <div className={styles.field}>
                <label htmlFor="store-timezone" className={styles.label}>
                  Time zone
                </label>
                <Select
                  id="store-timezone"
                  value={storeForm.timezone || inferTimezone(storeForm)}
                  onChange={(e) => setStoreForm({ ...storeForm, timezone: e.target.value })}
                  disabled={submitting || storeLocked}
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </Select>
              </div>
              <div className={styles.field}>
                <label htmlFor="store-description" className={styles.label}>
                  Store description
                </label>
                <textarea
                  id="store-description"
                  className={styles.textarea}
                  value={storeForm.description}
                  onChange={(e) => setStoreForm({ ...storeForm, description: e.target.value })}
                  disabled={submitting || storeLocked}
                  rows={4}
                  placeholder="Store type, aisles, square footage, hours, floors"
                />
              </div>
              <div className={styles.footer}>
                <div className={styles.footerLeft}>
                  <button
                    type="button"
                    onClick={leaveWizard}
                    className="btn btn-link"
                    style={{ color: "inherit", textDecoration: "none", padding: 0 }}
                  >
                    ← Back to your stores
                  </button>
                </div>
                <Button type="submit" disabled={submitting || storeForm.name.trim() === ""}>
                  {submitting ? "Creating…" : "Continue →"}
                </Button>
              </div>
            </form>
          )}

          {step === 2 && store && (
            <form className={styles.form} onSubmit={handleHardware} noValidate>
              <aside className={styles.banner} aria-label="Edge agent device requirement">
                <div>
                  <div className={styles.bannerTitle}>You'll need an edge agent device</div>
                  <div className={styles.bannerBody}>
                    Our service needs an internet-connected computer running 24/7 at your store
                    (Edge Agent). If you have a spare computer running Debian, Ubuntu, or Raspbian
                    operating system, please use it as the edge agent. Otherwise, order our
                    dedicated Edge Agent for <strong>$199</strong> plus shipping. Ordered during
                    your free trial, the $199 is a fully refundable deposit: cancel in the trial,
                    send the box back, and it comes back to you.
                  </div>
                </div>
                <a
                  className={styles.orderLink}
                  href={`/shopping?store=${store.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Order one →
                </a>
              </aside>
              {error && (
                <div className={styles.banner}>
                  <div>
                    <div className={styles.bannerTitle}>Couldn't set up the edge agent</div>
                    <div className={styles.bannerBody}>{error}</div>
                  </div>
                </div>
              )}
              <div className={styles.field}>
                <label htmlFor="agent-name" className={styles.label}>
                  Edge agent name
                </label>
                <Input
                  id="agent-name"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  disabled={submitting || edgeAgent !== null}
                  required
                />
              </div>
              {edgeAgent && (
                <Body>
                  <Muted>
                    This agent is already provisioned — hardware changes happen in the store's
                    Agents panel after setup. Continue to move on.
                  </Muted>
                </Body>
              )}
              <HardwareFields
                state={hardware}
                onChange={setHardware}
                busy={submitting || edgeAgent !== null}
              />
              <div className={styles.footer}>
                <div className={styles.footerLeft}>
                  <button
                    type="button"
                    onClick={() => navigate("/stores")}
                    className={styles.textBtn}
                  >
                    Save & exit
                  </button>
                </div>
                <Button
                  type="submit"
                  disabled={
                    submitting ||
                    (!edgeAgent && (agentName.trim() === "" || resolveHardware(hardware) === null))
                  }
                >
                  {submitting ? "Provisioning…" : "Continue →"}
                </Button>
              </div>
            </form>
          )}

          {step === 3 && store && (
            <ConnectEdgeAgentStep
              apiKey={edgeApiKey}
              agentConnected={edgeAgent !== null}
              isCustomHardware={isCustomHardware}
              provisioning={submitting}
              error={provisionError}
              storeName={store.name}
              acknowledged={edgeKeyAcknowledged}
              onAcknowledge={() => void acknowledgeKey()}
              online={online}
              checkingOnline={checkingOnline}
              onCheckOnline={() => void refreshOnline()}
              onRetry={() => setStep(2)}
              onContinue={() => void goToDescribe()}
              onFinishLater={() => navigate("/stores")}
            />
          )}

          {step === 4 && store && (
            <form className={styles.form} onSubmit={handleCameraDetails} noValidate>
              {error && (
                <div className={styles.banner}>
                  <div>
                    <div className={styles.bannerTitle}>Couldn't save camera details</div>
                    <div className={styles.bannerBody}>{error}</div>
                  </div>
                </div>
              )}

              <section className={styles.detailCard} aria-label="Store-wide watch instructions">
                <div>
                  <div className={styles.detailTitle}>What to watch for across the whole store</div>
                  <p className={styles.help}>
                    These rules apply to every camera. Rules about one camera go on that camera's
                    card below. Each one is reviewed before it takes effect, and you can add or
                    remove them later under the store's Settings.
                  </p>
                </div>
                <WatchInstructionsEditor scope={{ kind: "store", id: store.id }} />
              </section>

              {cameras.length === 0 ? (
                <div className={styles.banner}>
                  <div>
                    <div className={styles.bannerTitle}>No cameras discovered yet</div>
                    <div className={styles.bannerBody}>
                      Once your edge agent is installed it auto-discovers the store's cameras. Come
                      back here from your stores list to describe them — your progress is saved.
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.detailList}>
                  {cameras.map((cam) => {
                    const detail = cameraDetails[cam.id] ?? emptyCameraDetail();
                    const otherCameras = cameras.filter((other) => other.id !== cam.id);
                    return (
                      <section key={cam.id} className={styles.detailCard} aria-label={cam.name}>
                        <div className={styles.detailHeader}>
                          <div>
                            <div className={styles.detailTitle}>{cam.name}</div>
                            <div className={styles.detailSub}>{cam.asset_id}</div>
                          </div>
                        </div>
                        <div className={styles.grid2}>
                          <div className={styles.field}>
                            <label htmlFor={`camera-${cam.id}-location`} className={styles.label}>
                              Physical location
                            </label>
                            <Input
                              id={`camera-${cam.id}-location`}
                              placeholder="Overhead between aisle one and two"
                              value={detail.location_label}
                              onChange={(e) =>
                                updateCameraDetail(cam.id, { location_label: e.target.value })
                              }
                              disabled={submitting}
                            />
                          </div>
                          <div className={styles.field}>
                            <label htmlFor={`camera-${cam.id}-looking`} className={styles.label}>
                              Looking at
                            </label>
                            <Input
                              id={`camera-${cam.id}-looking`}
                              placeholder="Cash register and nearby area"
                              value={detail.looking_at}
                              onChange={(e) =>
                                updateCameraDetail(cam.id, { looking_at: e.target.value })
                              }
                              disabled={submitting}
                            />
                          </div>
                        </div>
                        {otherCameras.length > 0 && (
                          <fieldset className={styles.checkboxGroup}>
                            <legend className={styles.label}>Shared coverage</legend>
                            <div className={styles.checkList}>
                              {otherCameras.map((other) => (
                                <label key={other.id} className={styles.checkItem}>
                                  <input
                                    type="checkbox"
                                    checked={detail.overlap_camera_ids.includes(other.id)}
                                    onChange={() => toggleOverlap(cam.id, other.id)}
                                    disabled={submitting}
                                  />
                                  <span>{other.name}</span>
                                </label>
                              ))}
                            </div>
                          </fieldset>
                        )}
                        <div className={styles.field}>
                          <label htmlFor={`camera-${cam.id}-description`} className={styles.label}>
                            Additional description
                          </label>
                          <textarea
                            id={`camera-${cam.id}-description`}
                            className={styles.textarea}
                            value={detail.additional_description}
                            onChange={(e) =>
                              updateCameraDetail(cam.id, {
                                additional_description: e.target.value,
                              })
                            }
                            disabled={submitting}
                            rows={3}
                          />
                        </div>
                        <WatchInstructionsEditor scope={{ kind: "camera", id: cam.id }} />
                      </section>
                    );
                  })}
                </div>
              )}

              <div className={styles.footer}>
                <div className={styles.footerLeft}>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    disabled={submitting}
                    className={styles.textBtn}
                  >
                    ← Back
                  </button>
                </div>
                {cameras.length === 0 ? (
                  <Button type="button" onClick={() => navigate("/stores")} disabled={submitting}>
                    Finish later
                  </Button>
                ) : (
                  <Button type="submit" disabled={submitting}>
                    {submitting ? "Saving…" : "Continue →"}
                  </Button>
                )}
              </div>
            </form>
          )}

          {step === 5 && store && (
            <>
              {error && (
                <div className={styles.banner}>
                  <div>
                    <div className={styles.bannerTitle}>Couldn't complete setup</div>
                    <div className={styles.bannerBody}>{error}</div>
                  </div>
                </div>
              )}
              <BillingStep
                store={store}
                allowSkip={allowBillingSkip}
                onComplete={() => void finishSetup()}
              />
            </>
          )}
        </main>
      </div>
    </Layout>
  );
};
